import React, { useEffect, useRef, useState } from 'react';
import Header from '@components/mobile-header';
import Footer from '@components/footer';
import Loading from '@components/loading';

import cs from 'classnames';
import { getWinHeight, openLinkInNewTab } from '@utils/util';

import { Divider, Empty, Message } from '@byted/serial-components-i18n';
import { useTranslation } from '@ies/intl-react-plugin';
import { useEventListener, useThrottleFn } from '@byted/hooks';
import { useUserInfo } from '@utils/hooks';

import { getRewardList } from '@service/api';
import { MessageText } from '@constants/value';
import { FORMAT, getTitleText, PAGE_SIZE, TagFlag, TagText, ValidateUrl } from '../setting';

import { dayjs } from '@utils/day';
import { Tea } from '@utils/tea';

import './index.less';

const PRE_LOAD_RATE = 0.2;

const Reward: React.FC = () => {
    // todo 加上类型声明
    const [activities, setActivities] = useState<any[]>();
    const [total, setTotal] = useState(0);
    const [pageIndex, setPageIndex] = useState(0);
    const [loading, setLoading] = useState(true);
    const [isLoadErr, setIsLoadErr] = useState(false);
    const [detailUrl, setDetailUrl] = useState('');
    const [lastLoadOffsetTop, setLastLoadOffsetTop] = useState<number>();

    const accountFn = useRef<any>();
    const loadRef = useRef<HTMLDivElement>(null);

    const { i18n } = useTranslation();
    const { media_id } = useUserInfo();

    const hasMore = total > pageIndex * PAGE_SIZE;

    useEffect(() => {
        getActivities();
    }, []);

    useEventListener('scroll', () => {
        const loadText = loadRef.current;
        const viewPortHeight = getWinHeight();
        if (loadText) {
            // 距离loading还有0.2个屏幕的时候提前加载
            const loadTextOffsetY = loadText.getBoundingClientRect().y;
            const isLoadingShow = viewPortHeight + viewPortHeight * PRE_LOAD_RATE >= loadTextOffsetY;
            const loadOffsetTop = loadText.offsetTop;

            // 可正常发送请求:
            // 1. 当存在更多可获取条目并且滚动到底部
            // 2. 没有网络错误，有网络错误提示文案，并需要用户手动点击重试
            // 3. loadText的高度不能上一次发送请求的高度一致（在loading完毕，list没渲染出来前可能会引起二次误触）
            if (hasMore && isLoadingShow && !isLoadErr && loadOffsetTop !== lastLoadOffsetTop) {
                setLastLoadOffsetTop(loadOffsetTop);
                throttleGetActivities();
            }
        }
    });

    // 补充类型声明
    const jumpToActivity = (activity: any) => {
        const link = activity.activity_link;
        const isValidate = ValidateUrl.some(url => {
            if (link.includes(url)) {
                setDetailUrl(url);
                return true;
            }
            return false;
        });
        if (isValidate && !media_id) {
            const setAccountVisible = accountFn.current;
            setAccountVisible(true);
        } else {
            setDetailUrl('');
            window.open(link);
        }
        Tea('enter_award', {
            award_name: activity.name,
        });
    };

    const getActivities = async () => {
        // activities不为undefined时即不是第一次加载列表，
        // 如果已经触发触底加载则不再重复发送请求
        if (activities && loading) {
            return;
        }
        setLoading(true);
        setIsLoadErr(false);

        try {
            const resp = await getRewardList({
                page_index: pageIndex,
                page_count: PAGE_SIZE,
                language: i18n.language,
            });
            if (resp.code) {
                throw Error(resp.message);
            }
            const origin_list = activities ? activities : [];
            setActivities(origin_list.concat(resp.data.activity_list));
            setPageIndex(pageIndex + 1);
            setTotal(resp.data.total_count);
        } catch (e) {
            setIsLoadErr(true);
            Message.error(MessageText.netError);
            console.log(e);
        } finally {
            setLoading(false);
        }
    };

    // 获取活动列表
    const { run: throttleGetActivities } = useThrottleFn(getActivities, 500);

    const handleLoginIn = () => {
        const setAccountVisible = accountFn.current;
        setAccountVisible(false);
        openLinkInNewTab(`${location.origin}/reward/${detailUrl}`);
    };

    const renderBottom = () => {
        let content;
        if (hasMore) {
            if (isLoadErr) {
                // 当网络异常时显示文案
                content = (
                    <div onClick={getActivities}>
                        <Divider>{i18n.t('reward_loadfail')}</Divider>
                    </div>
                );
            } else {
                // 无异常时，触底加载显示loading
                content = (
                    <div className="load-more-wrap">
                        <Loading className="load-more-icon" />
                        <span style={{ cursor: 'pointer' }}>{i18n.t('notice_load_more')}</span>
                    </div>
                );
            }
        } else {
            // 当没有更多可以加载的条目后显示加载完
            content = <Divider>{i18n.t('reward_staytuned')}</Divider>;
        }
        return (
            <div className="reward-list-bottom" ref={loadRef}>
                {content}
            </div>
        );
    };

    const showList = activities && activities.length > 0;

    if (!showList && loading) {
        return <Loading className="reward-loading" />;
    }

    return (
        <div className={cs('reward', { 'reward-empty': !showList })}>
            <Header
                getAccountVisibleFn={fn => (accountFn.current = fn)}
                loginCallback={detailUrl ? handleLoginIn : undefined}
            />
            <div className="reward-body">
                <h3>{i18n.t('reward_exit')}</h3>
                {showList ? (
                    <>
                        <div className="reward-list">
                            {activities.map(activity => (
                                <div
                                    className="reward-list-item"
                                    key={activity.id}
                                    onClick={() => jumpToActivity(activity)}
                                >
                                    <img src={activity.picture_link} alt="" />
                                    <div className="reward-list-item-content">
                                        <h4>
                                            {getTitleText(activity.name)}&nbsp;
                                            <i className={`tag-flag tag-flag--${TagFlag[activity.time_status]}`}>
                                                {i18n.t(TagText[activity.time_status])}
                                            </i>
                                        </h4>
                                        <p>{activity.description}</p>
                                        <span>
                                            {i18n.t('reward_periode')}:{' '}
                                            {dayjs.utc(activity.start_time * 1000).format(FORMAT)}-
                                            {dayjs.utc(activity.end_time * 1000).format(FORMAT)}(
                                            {i18n.t('chapter_published_utc0')})
                                            <br />
                                            {activity.bonus}
                                        </span>
                                    </div>
                                </div>
                            ))}
                        </div>
                        {renderBottom()}
                    </>
                ) : (
                    <Empty description={i18n.t('reward_staytuned')} />
                )}
            </div>
            <Footer />
        </div>
    );
};

export default Reward;
